/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow

import { MIN_QUERY_LENGTH } from './autocompleteSuggestionsRequest.js';

export const STATUS_IDLE = 'idle';
export const STATUS_LOADING = 'loading';
export const STATUS_OPEN = 'open';
export const STATUS_EMPTY = 'empty';
export const STATUS_ERROR = 'error';

const INPUT_CHANGED = 'INPUT_CHANGED';
const SUGGESTIONS_RECEIVED = 'SUGGESTIONS_RECEIVED';
const SUGGESTIONS_FAILED = 'SUGGESTIONS_FAILED';
const HIGHLIGHT_NEXT = 'HIGHLIGHT_NEXT';
const HIGHLIGHT_PREVIOUS = 'HIGHLIGHT_PREVIOUS';
const HIGHLIGHT_AT = 'HIGHLIGHT_AT';
const DISMISSED = 'DISMISSED';
const SELECTED = 'SELECTED';

const NOTHING_HIGHLIGHTED = -1;

export type TypeaheadState = {
  query: string,
  suggestions: Array<Object>,
  highlightedIndex: number,
  status: string,
};

export const initialState: TypeaheadState = {
  query: '',
  suggestions: [],
  highlightedIndex: NOTHING_HIGHLIGHTED,
  status: STATUS_IDLE,
};

export const inputChanged = (query: string): Object => ({ type: INPUT_CHANGED, query });
export const suggestionsReceived = (query: string, suggestions: Array<Object>): Object => ({
  type: SUGGESTIONS_RECEIVED,
  query,
  suggestions,
});
export const suggestionsFailed = (query: string): Object => ({ type: SUGGESTIONS_FAILED, query });
export const highlightNext = (): Object => ({ type: HIGHLIGHT_NEXT });
export const highlightPrevious = (): Object => ({ type: HIGHLIGHT_PREVIOUS });
export const highlightAt = (index: number): Object => ({ type: HIGHLIGHT_AT, index });
export const dismissed = (): Object => ({ type: DISMISSED });
export const selected = (): Object => ({ type: SELECTED });

const closed = state => ({
  ...state,
  suggestions: [],
  highlightedIndex: NOTHING_HIGHLIGHTED,
  status: STATUS_IDLE,
});

const typeaheadReducer = (state: TypeaheadState, action: Object): TypeaheadState => {
  switch (action.type) {
    case INPUT_CHANGED: {
      const tooShort = action.query.length < MIN_QUERY_LENGTH;
      return {
        ...state,
        query: action.query,
        suggestions: [],
        highlightedIndex: NOTHING_HIGHLIGHTED,
        status: tooShort ? STATUS_IDLE : STATUS_LOADING,
      };
    }
    case SUGGESTIONS_RECEIVED: {
      // Dismissing preserves the query, so status is what distinguishes a response the user is
      // still waiting for from one that arrives after they closed the list.
      if (action.query !== state.query || state.status !== STATUS_LOADING) {
        return state;
      }
      return {
        ...state,
        suggestions: action.suggestions,
        highlightedIndex: NOTHING_HIGHLIGHTED,
        status: action.suggestions.length ? STATUS_OPEN : STATUS_EMPTY,
      };
    }
    case SUGGESTIONS_FAILED: {
      if (action.query !== state.query || state.status !== STATUS_LOADING) {
        return state;
      }
      return {
        ...state,
        suggestions: [],
        highlightedIndex: NOTHING_HIGHLIGHTED,
        status: STATUS_ERROR,
      };
    }
    case HIGHLIGHT_NEXT: {
      if (state.status !== STATUS_OPEN) {
        return state;
      }
      const last = state.suggestions.length - 1;
      return { ...state, highlightedIndex: Math.min(state.highlightedIndex + 1, last) };
    }
    case HIGHLIGHT_PREVIOUS: {
      if (state.status !== STATUS_OPEN) {
        return state;
      }
      return {
        ...state,
        highlightedIndex: Math.max(state.highlightedIndex - 1, NOTHING_HIGHLIGHTED),
      };
    }
    case HIGHLIGHT_AT: {
      if (state.status !== STATUS_OPEN) {
        return state;
      }
      const { index } = action;
      if (index < 0 || index >= state.suggestions.length) {
        return state;
      }
      return { ...state, highlightedIndex: index };
    }
    case DISMISSED:
    case SELECTED:
      return closed(state);
    default:
      return state;
  }
};

export const selectionIndexForEnter = (state: TypeaheadState): number | null => {
  if (state.status !== STATUS_OPEN) {
    return null;
  }
  const { highlightedIndex, suggestions } = state;
  if (highlightedIndex < 0 || highlightedIndex >= suggestions.length) {
    return null;
  }
  return highlightedIndex;
};

export default typeaheadReducer;
